<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { marketingAnalytics } from '@/services/marketingAnalytics'

declare const __APP_BUILD_HASH__: string
declare const __APP_BUILD_TIME__: number

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

interface VersionPayload {
  buildHash: string
  buildTime: number
  timestamp: string
}

const INSTALL_DISMISS_KEY = 'venmas:pwa-install-dismissed-at'
const INSTALL_ACCEPTED_KEY = 'venmas:pwa-installed'
const INSTALL_RESHOW_DAYS = 7
const VERSION_POLL_MS = 60_000
const isDev = import.meta.env.DEV

const currentBuildHash = __APP_BUILD_HASH__
const currentBuildTime = __APP_BUILD_TIME__

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const showInstallPrompt = ref(false)
const isOffline = ref(typeof navigator !== 'undefined' ? !navigator.onLine : false)
const isInstalled = ref(false)
const isReloadingForUpdate = ref(false)
const registration = ref<ServiceWorkerRegistration | null>(null)

let versionPollTimer: ReturnType<typeof setInterval> | null = null

async function clearBrowserCaches() {
  if (!('caches' in window)) return
  const keys = await caches.keys()
  await Promise.all(keys.map((key) => caches.delete(key)))
}

async function unregisterServiceWorkers() {
  if (!('serviceWorker' in navigator)) return
  const registrations = await navigator.serviceWorker.getRegistrations()
  await Promise.all(registrations.map((entry) => entry.unregister()))
}

const installPromptEligible = computed(() =>
  !isInstalled.value &&
  !!deferredPrompt.value
)

function dismissInstallPrompt(source = 'unknown') {
  showInstallPrompt.value = false
  localStorage.setItem(INSTALL_DISMISS_KEY, Date.now().toString())
  marketingAnalytics.track('pwa_prompt_dismissed', {
    element_id: `pwa_dismiss_${source}`,
    label: 'PWA prompt dismissed',
    metadata: { source },
  })
}

async function installApp() {
  if (!deferredPrompt.value) return

  showInstallPrompt.value = false
  marketingAnalytics.track('pwa_install_clicked', { element_id: 'pwa_install_button', label: 'Install PWA' })
  await deferredPrompt.value.prompt()
  const choice = await deferredPrompt.value.userChoice

  marketingAnalytics.track('pwa_install_result', {
    element_id: `pwa_install_${choice.outcome}`,
    label: choice.outcome,
    metadata: { outcome: choice.outcome, platform: choice.platform },
  })

  if (choice.outcome === 'accepted') {
    localStorage.setItem(INSTALL_ACCEPTED_KEY, 'true')
    isInstalled.value = true
  } else {
    localStorage.setItem(INSTALL_DISMISS_KEY, Date.now().toString())
  }

  deferredPrompt.value = null
}

function shouldShowInstallPrompt() {
  if (localStorage.getItem(INSTALL_ACCEPTED_KEY) === 'true') return false

  const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  if (standalone) return false

  const dismissedAt = Number(localStorage.getItem(INSTALL_DISMISS_KEY) || 0)
  if (!dismissedAt) return true

  const elapsedDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24)
  return elapsedDays >= INSTALL_RESHOW_DAYS
}

function handleBeforeInstallPrompt(event: Event) {
  event.preventDefault()
  deferredPrompt.value = event as BeforeInstallPromptEvent
  marketingAnalytics.track('pwa_prompt_available', { label: 'PWA install prompt available' })

  if (shouldShowInstallPrompt()) {
    showInstallPrompt.value = true
    marketingAnalytics.track('pwa_prompt_shown', { label: 'PWA install prompt shown' })
  }
}

function handleAppInstalled() {
  localStorage.setItem(INSTALL_ACCEPTED_KEY, 'true')
  isInstalled.value = true
  showInstallPrompt.value = false
  deferredPrompt.value = null
  marketingAnalytics.track('pwa_appinstalled', { label: 'PWA app installed' })
}

function handleOnline() {
  isOffline.value = false
}

function handleOffline() {
  isOffline.value = true
}

async function fetchVersion(): Promise<VersionPayload | null> {
  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'cache-control': 'no-cache',
      },
    })

    if (!response.ok) return null
    return response.json()
  } catch {
    return null
  }
}

async function triggerUpdateReload() {
  if (isReloadingForUpdate.value) return
  isReloadingForUpdate.value = true

  try {
    if (registration.value) {
      await registration.value.update()

      if (registration.value.waiting) {
        registration.value.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
    }

    await clearBrowserCaches()
    await unregisterServiceWorkers()
  } finally {
    const nextUrl = new URL(window.location.href)
    nextUrl.searchParams.set('app_build', String(Date.now()))
    window.location.replace(nextUrl.toString())
  }
}

async function checkForNewBuild() {
  const version = await fetchVersion()
  if (!version) return

  const hashChanged = version.buildHash && version.buildHash !== currentBuildHash
  const timeChanged = Number(version.buildTime || 0) > Number(currentBuildTime || 0)

  if (hashChanged || timeChanged) {
    await triggerUpdateReload()
  }
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return

  try {
    let hadController = !!navigator.serviceWorker.controller
    registration.value = await navigator.serviceWorker.register('/sw.js')
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) {
        hadController = true
        return
      }
      if (!isReloadingForUpdate.value) {
        isReloadingForUpdate.value = true
        window.location.reload()
      }
    })
  } catch (error) {
    console.error('Failed to register service worker:', error)
  }
}

function startVersionPolling() {
  versionPollTimer = setInterval(() => {
    if (!document.hidden) {
      void checkForNewBuild()
    }
  }, VERSION_POLL_MS)
}

function stopVersionPolling() {
  if (versionPollTimer) {
    clearInterval(versionPollTimer)
    versionPollTimer = null
  }
}

function handleVisibilityChange() {
  if (!document.hidden) {
    void checkForNewBuild()
  }
}

function handleWindowFocus() {
  void checkForNewBuild()
}

onMounted(async () => {
  if (isDev) {
    await unregisterServiceWorkers()
    await clearBrowserCaches()
    return
  }

  isInstalled.value = localStorage.getItem(INSTALL_ACCEPTED_KEY) === 'true'

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  window.addEventListener('focus', handleWindowFocus)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  await registerServiceWorker()
  startVersionPolling()
  void checkForNewBuild()
})

onUnmounted(() => {
  stopVersionPolling()
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.removeEventListener('appinstalled', handleAppInstalled)
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  window.removeEventListener('focus', handleWindowFocus)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="showInstallPrompt && installPromptEligible"
      class="fixed inset-0 z-[120] flex items-end justify-center bg-black/45 p-4 backdrop-blur-sm sm:items-center"
      @click="dismissInstallPrompt('backdrop')"
    >
      <div
        class="w-full max-w-md rounded-3xl border border-white/20 bg-white/95 p-5 shadow-2xl backdrop-blur-md"
        @click.stop
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex items-start gap-4">
            <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-600 shadow-lg shadow-blue-500/20">
              <img
                src="/icons/app-icon.svg"
                alt="Venmas app icon"
                class="h-10 w-10 object-contain"
              >
            </div>
            <div>
              <h3 class="text-lg font-bold text-gray-900">Install Venmas App</h3>
              <p class="mt-1 text-sm text-gray-600">
                Add Venmas to your home screen for faster access and a more app-like experience.
              </p>
            </div>
          </div>
          <button
            class="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            @click="dismissInstallPrompt('close')"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="mt-5 flex items-center justify-end gap-3">
          <button
            class="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            @click="dismissInstallPrompt('not_now')"
          >
            Not now
          </button>
          <button
            class="rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-transform hover:scale-[1.01]"
            @click="installApp"
          >
            Install
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isOffline"
      class="fixed inset-x-4 bottom-4 z-[110] sm:left-auto sm:right-4 sm:w-auto"
    >
      <div class="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-lg">
        <span class="inline-flex h-3 w-3 rounded-full bg-amber-500"></span>
        <div>
          <p class="text-sm font-semibold text-amber-900">No internet connection</p>
          <p class="text-xs text-amber-800">Some live platform features may be unavailable until you reconnect.</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>
